import React, { FC, useState, useEffect } from "react";
import {
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  Tooltip,
} from "@mui/material";
import {
  LocalizationProvider,
  DateRangePicker,
  SingleInputDateRangeField,
  CalendarIcon,
} from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FormFieldProps } from "../../../utils/formFields";
import useStyles from "../../../components/common/model/styles";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useSelector } from "react-redux";
dayjs.extend(utc);
dayjs.extend(timezone);
const DateRangeField: FC<FormFieldProps> = (props) => {
  const classes = useStyles();
  const { userProfile } = useSelector((state: any) => state?.user);
  /** * Use user's configured timezone if available. * Fallback keeps existing UK behaviour. */ const appTimezone =
    userProfile?.timezone || "Europe/London";
  const [selectedDates, setSelectedDates] = useState<
    [Dayjs | null, Dayjs | null]
  >([null, null]);
  /** * Convert API UTC date into the user's configured timezone. * * This is only for the DatePicker value. * API value remains UTC ISO as before. */ const parseDate =
    (value?: string | null): Dayjs | null => {
      if (!value) {
        return null;
      }
      return dayjs.utc(value).tz(appTimezone);
    };
  /** * Sync picker state when parent value changes. */ useEffect(() => {
    if (!props.value) {
      setSelectedDates([null, null]);
      return;
    }
    console.log("props.value:", props.value);
    if (typeof props.value === "string") {
      setSelectedDates([parseDate(props.value), null]);
      return;
    }
    if (Array.isArray(props.value) && props.value.length === 2) {
      setSelectedDates([
        props.value[0] ? parseDate(props.value[0]) : null,
        props.value[1] ? parseDate(props.value[1]) : null,
      ]);
    }
  }, [props.value, appTimezone]);
  /** * Handle DateRangePicker changes. * * IMPORTANT: * We are not changing the existing API behaviour. * We only normalize start/end to the same timezone. */ const handleDateChange =
    (dateRange: [Dayjs | null, Dayjs | null]) => {
      console.log("========== DATE CHANGE ==========");
      console.log("dateRange:", dateRange);
      console.log("start:", dateRange[0]);
      console.log("end:", dateRange[1]);
      console.log("previous selectedDates:", selectedDates);
      console.log("appTimezone:", appTimezone);
      const [start, end] = dateRange;
      /** * Normalize both picker values to the same timezone. * * This helps prevent: * MUI X: The timezone of the start and the end * date should be the same. */ const newStart =
        start ? start.tz(appTimezone) : selectedDates[0];
      const newEnd = end ? end.tz(appTimezone) : null;
      console.log("newStart:", newStart);
      console.log("newEnd:", newEnd);
      const updatedDateRange: [Dayjs | null, Dayjs | null] = [newStart, newEnd];
      setSelectedDates(updatedDateRange);
      let formattedValue: string | string[] | null = null;
      /** * Keep the existing API format. * * Single date: * "2026-10-01T00:00:00.000Z" * * Range: * [ * "2026-10-01T00:00:00.000Z", * "2026-10-17T00:00:00.000Z" * ] */ if (
        newStart &&
        !newEnd
      ) {
        formattedValue = dayjs.utc(newStart.format("YYYY-MM-DD")).toISOString();
      } else if (newStart && newEnd) {
        formattedValue = [
          dayjs.utc(newStart.format("YYYY-MM-DD")).toISOString(),
          dayjs.utc(newEnd.format("YYYY-MM-DD")).toISOString(),
        ];
      }
      console.log("formattedValue:", formattedValue);
      props?.onChange?.({ target: { id: props.id, value: formattedValue } });
    };
  const inputRender = () => {
    return (
      <DateRangePicker
        slots={{ field: SingleInputDateRangeField }}
        sx={{
          width: props?.width ?? "100%",
          background: "#fff",
          "&.MuiFormControl-root": { borderRadius: "5px" },
        }}
        value={selectedDates}
        format={userProfile?.dateFormat ? userProfile.dateFormat : "DD/MM/YYYY"}
        disablePast={props?.disablePast || false}
        disabled={props?.disabled || false}
        onChange={handleDateChange}
        localeText={{ start: "", end: "" }}
        slotProps={{
          textField: {
            size: "small",
            error: props.error,
            InputProps: {
              inputRef: props.inputRef,
              readOnly: true,
              sx: {
                height: props?.heightVal ?? "36px",
                border: props.error ? "1px solid #d32f2f" : undefined,
                paddingRight: "11px",
              },
              endAdornment: (
                <InputAdornment position="end">
                  {" "}
                  <CalendarIcon
                    sx={{
                      color: "#888",
                      height: props?.iconCalender ? props.iconCalender : "auto",
                      cursor: "pointer",
                    }}
                  />{" "}
                </InputAdornment>
              ),
            },
          },
        }}
      />
    );
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {" "}
      {props?.label && !props?.inSingleGridForm ? (
        <>
          {" "}
          <Grid
            item
            xs={12}
            sm={props?.md ? (props?.firstColmd ? props.firstColmd : 1.5) : 5}
            sx={{ marginTop: props?.isNonMarginTop ? "0px !important" : "" }}
          >
            {" "}
            <InputLabel
              sx={{ color: "#333333", fontWeight: 600, fontSize: "18px" }}
              shrink
              htmlFor={props?.id}
            >
              {" "}
              {props?.label}{" "}
              {props?.isRequired && (
                <span style={{ color: "red", fontWeight: 500 }}> * </span>
              )}{" "}
            </InputLabel>{" "}
          </Grid>{" "}
          <Grid
            item
            xs={12}
            sm={props?.md ? props.md : 7}
            style={{
              padding: "0px",
              marginTop: props?.isNonMarginTop ? "0px" : "16px",
              marginBottom: props?.isNonMarginBottom ? "0px" : "8px",
            }}
          >
            {" "}
            {inputRender()}{" "}
            {props.error && (
              <FormHelperText sx={{ color: "#d32f2f", marginLeft: "14px" }}>
                {" "}
                {props.helperText}{" "}
              </FormHelperText>
            )}{" "}
          </Grid>{" "}
        </>
      ) : props?.label && props?.inSingleGridForm ? (
        <Grid
          item
          xs={12}
          sm={props?.md ? props.md : 12}
          sx={{
            display: "block",
            alignItems: "center",
            gap: 2,
            ...props?.GridFieldStyle,
          }}
        >
           <Tooltip title={props?.tooltipText || ""} arrow>
            <InputLabel
              sx={{
                color: "#637381",
                fontWeight: 600,
                fontSize: "16px",
                textAlign: "left !important",
              }}
              shrink
              htmlFor={props?.id}
            >
              {" "}
              {props?.label}{" "}
              {props?.isRequired && (
                <span style={{ color: "red", fontWeight: 500 }}> * </span>
              )}{" "}
            </InputLabel>
          </Tooltip>
          {inputRender()}{" "}
        </Grid>
      ) : (
        inputRender()
      )}{" "}
    </LocalizationProvider>
  );
};
export default DateRangeField;
